/**
 * @file index.tsx
 *
 * @fileoverview The page for the bundles picker in the job wizard.
 */

import React from 'react';
import { GetServerSideProps, NextPage } from 'next';
import { withoutAuthenticateApplicant } from '~/jobiqo/hocs/withoutAuthenticate/applicant';
import JobWizardBundles, {
  JobWizardBundlesProps,
} from '~/jobiqo/components/Job/Wizard/Steps/Bundles';
import initApollo from '~/jobiqo/apollo/initApollo';
import GET_JOB_BUNDLES from '~/jobiqo/components/Job/Wizard/Steps/Bundles/graphql/queries/jobBundles.gql';
import GET_DEFAULT_PRODUCT from '~/jobiqo/pages/job/wizard/bundles/graphql/queries/getDefaultProduct.gql';
import GET_JOB_TYPE_BY_JOB_ID from '~/jobiqo/pages/job/wizard/bundles/graphql/queries/getJobTypeByJobId.gql';
import { getStepsinfo } from '~/jobiqo/components/Job/Wizard/config';
import { JobWizardFlow } from '~/jobiqo/components/Job/Wizard/config';
import { JobWizardContextProvider } from '~/jobiqo/components/Job/Wizard/context/useJobWizardContext';
import { gsspRedirect } from '~/jobiqo/lib/redirect';
import { JobBundle } from '~/jobiqo/models/jobs';
import { sortByWeight } from '~/jobiqo/utils/sort';
import GET_FLATRATE_FRONTENDS from '~/jobiqo/components/Job/Wizard/Steps/Destination/graphql/queries/getFlatRateFrontends.gql';
import { getOrganizationProfileByUserIdQuery } from '~/jobiqo/components/Organization/queries/getOragizationQuery';
import { getUser } from '~/jobiqo/lib/auth';
import { getFrontend } from '~/jobiqo/utils/getFrontend';
import { getServerFrontendConfig } from '~/jobiqo/lib/frontendConfig';

const JobWizardBundlesPage: NextPage<JobWizardBundlesProps> = (props) => (
  <JobWizardContextProvider>
    <JobWizardBundles {...props} />
  </JobWizardContextProvider>
);

export const getServerSideProps: GetServerSideProps = withoutAuthenticateApplicant(
  {
    gssp: async function (ctx) {
      const frontendConfig = getServerFrontendConfig(ctx);
      const JOB_POSTING_WIZARD_SKIP_BUNDLES_CURRENT_INCLUDED =
        frontendConfig.JOBS.JOB_POSTING_WIZARD_SKIP_BUNDLES_CURRENT_INCLUDED;
      const JOB_POSTING_WIZARD_SKIP_BUNDLES_ALL_INCLUDED =
        frontendConfig.JOBS.JOB_POSTING_WIZARD_SKIP_BUNDLES_ALL_INCLUDED;
      const queryParams = ctx.resolvedUrl.split('?')?.[1] ?? null;
      const user = await getUser(ctx);
      let skipStep = false;

      const [jobId] = ctx.query.params ?? [];

      const apolloClient = initApollo({}, ctx);

      const { error, data, loading } = await apolloClient.query({
        query: GET_JOB_BUNDLES,
        variables: {
          locale: ctx.locale,
        },
      });

      if (JOB_POSTING_WIZARD_SKIP_BUNDLES_ALL_INCLUDED && !!user?.id) {
        const { data: flatrateData } = await apolloClient.query({
          query: GET_FLATRATE_FRONTENDS,
          variables: {
            locale: ctx.locale,
          },
        });

        const { data: organizationData } = await apolloClient.query({
          query: getOrganizationProfileByUserIdQuery(),
          variables: {
            id: user?.id,
          },
        });

        const currentFrontend = getFrontend(frontendConfig);

        const hasFlatrateForFrontend = flatrateData?.jobFlatrateFrontends?.includedFrontends?.includes(
          currentFrontend,
        );

        // If there is only 1 frontend and for that one there is a flatrate,
        // then we can redirect to the destination step.
        if (
          organizationData?.organizationProfileByUserId?.content?.frontends
            ?.length === 1 &&
          hasFlatrateForFrontend
        ) {
          skipStep = true;
        }

        // If JOB_POSTING_WIZARD_SKIP_BUNDLES_CURRENT_INCLUDED is enabled and
        // user has flatrate for the current frontend, skip this step.
        if (
          JOB_POSTING_WIZARD_SKIP_BUNDLES_CURRENT_INCLUDED &&
          hasFlatrateForFrontend
        ) {
          skipStep = true;
        }
      }

      // If there are no job bundles, skip this step.
      if ((data?.jobBundles.length === 0 && !error) || ctx.query.bundle) {
        skipStep = true;
      }

      if (skipStep) {
        return {
          props: {},
          redirect: {
            destination: gsspRedirect(
              ctx,
              jobId
                ? `${
                    getStepsinfo(
                      JobWizardFlow.Republish,
                      false,
                    )?.[0].page.split('[')[0]
                  }/${jobId}${queryParams ? '?' + queryParams : ''}`
                : `${getStepsinfo(JobWizardFlow.New, false)?.[0].page}${
                    queryParams ? '?' + queryParams : ''
                  }`,
            ),
          },
        };
      }

      const {
        error: defaultProductError,
        data: defaultProductData,
        loading: defaultProductLoading,
      } = await apolloClient.query({
        query: GET_DEFAULT_PRODUCT,
        variables: {
          language: ctx.locale,
        },
      });

      // Create a copy of job bundles array.
      const jobBundles: JobBundle[] = Array.from(data.jobBundles);

      // Add default product to the start of a job bundles array.
      // Do this only if the bundles doesn't already contain the product.
      if (
        !defaultProductError &&
        defaultProductData?.getDefaultProduct?.id &&
        !jobBundles.some(
          (product) => product.id === defaultProductData?.getDefaultProduct?.id,
        )
      ) {
        jobBundles.unshift({
          ...defaultProductData.getDefaultProduct,
          isDefault: true,
        });
      }

      let jobType: string = '';

      if (jobId) {
        const { data, error } = await apolloClient.query({
          query: GET_JOB_TYPE_BY_JOB_ID,
          variables: {
            jobId: Number(jobId),
          },
        });

        if (!error && data?.jobById?.content?.type) {
          jobType = data.jobById.content.type;
        }
      }

      return {
        props: {
          // Pass the bundles containing default product.
          data: { ...data, jobBundles: jobBundles.sort(sortByWeight) },
          error: !!error,
          loading: loading || defaultProductLoading,
          flow: !!jobId ? JobWizardFlow.Republish : JobWizardFlow.New,
          jobId: Number(jobId),
          jobType,
        },
      };
    },
  },
);

export default JobWizardBundlesPage;
